import { defineStore } from 'pinia'   
import { computed, ref, Ref } from 'vue'
import { useUserStore } from './user'
import { ClassificationApi, DEBUG, DEBUG_WARN } from '@/util'

export interface PropertyValueDTO {
    propertyName: string,
    propertyValue: string,
    propertyValueText: string,
    parentPropertyValue: null | string,
}

export type dictOfPropMaps = {
    [key: string]: Map<string, PropertyValueDTO>
}

/** store of localized classification properties and values */
export const useClassificationStore = defineStore('classificationStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _language: Ref<string> = ref('');
    const _classificationData: Ref<null | dictOfPropMaps> = ref(null);

    /********************************************************************************************************************
     PRIVATE FUNCTIONS
     ********************************************************************************************************************/
    /** groups the specified data by property name, each group containing a map indexed by property values */
    function groupAndMap(data: PropertyValueDTO[]) {
        const propertyNameLookup = data.toLookup(d => d.propertyName)

        const result: dictOfPropMaps = {}
        const propertyNames = Array.from(propertyNameLookup.keys())

        for (const propName of propertyNames) {
            result[propName] = propertyNameLookup.get(propName)?.toMap(pv => pv.propertyValue) ?? new Map()
        }

        return result
    }

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    /** currently active language */
    const language = computed(() => _language.value);

    const hasLoaded = computed(() => _classificationData.value != null)

    const getters = { language, hasLoaded }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Sets the currently active language and automatically reloads data */
    function setLanguage(language: string) {
        if (language != _language.value) {
            _language.value = language;
            loadClassificationData();
        }
    }

    /** Forces reload of the data of the currently set language */
    function loadClassificationData() {
        DEBUG('ClassificationStore: Fetching classification data...');
        const api = new ClassificationApi

        api.get(_language.value)
            .then(response => _classificationData.value = groupAndMap(response.data))
            .catch(error => DEBUG_WARN('ClassificationStore: Failed to load classification data:', error));
    }

    /** Returns the localized classification (property values) data related to the specified property value ID */
    function get(propertyName: string, propertyValue?: string | null): PropertyValueDTO | null {
        if (!propertyValue) return null;

        const data = _classificationData?.value;

        if (data) {
            return data[propertyName].get(propertyValue) ?? null;
        }

        return null;
    }

    /** returns an array containing the propertyValue of each node in the property value hierarchy 
     * ending at the specified property value */
    function getHierarchy(propertyName: string, propertyValue?: string | null): PropertyValueDTO[] {
        const result: PropertyValueDTO[] = []

        const data = _classificationData?.value

        if (propertyValue && data) {
            const dict = data[propertyName]

            if (dict) {
                let dto = dict.get(propertyValue)

                while (dto) {
                    result.push(dto)
                    dto = dto.parentPropertyValue == null
                        ? undefined
                        : dict.get(dto.parentPropertyValue)
                }
            }
        }

        return result.reverse()
    }

    /** returns an array containing the localized text of each node in the property value hierarchy 
     * ending at the specified property value */
    function getHierarchyTexts(propertyName: string, propertyValue?: string | null): string[] {
        const result: string[] = [];

        const data = _classificationData?.value;

        if (propertyValue && data) {
            const dict = data[propertyName]

            if (dict) {
                let dto = dict.get(propertyValue);

                while (dto) {
                    result.push(dto.propertyValueText);                        
                    dto = dto.parentPropertyValue == null
                        ? undefined
                        : dict.get(dto.parentPropertyValue);
                }
            }
        }

        return result.reverse();
    }

    /**
     * Returns all property values of the specified property
     * @param propertyName Name of the property for which to retrieve all property values
     */
    function getPropertyValues(propertyName: string): string[] {
        const result: string[] = []
        const data = _classificationData?.value

        if (data) {
            const dict = data[propertyName]

            dict.forEach((value) => {
                result.push(value.propertyValue)
            });
        }

        return result
    }

    /**
     * Returns all property value DTOs of the specified property
     * @param propertyName Name of the property for which to retrieve all property value DTOs
     */
    function getPropertyValueDTOs(propertyName: string): PropertyValueDTO[] {
        const result: PropertyValueDTO[] = []
        const data = _classificationData?.value

        if (data) {
            const dict = data[propertyName]

            dict.forEach((value) => {
                result.push(value)
            })
        }

        return result
    }

    const actions = { setLanguage, loadClassificationData, get, getHierarchy, getHierarchyTexts, getPropertyValues, getPropertyValueDTOs }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    const userStore = useUserStore();

    userStore.$subscribe((mutation, state) => {
        setLanguage(state._language)
    });

    if (!language.value) {
        setLanguage(userStore.language);
    }

    return { ...getters, ...actions }
})